/**
 * ObsidianWeb — top-level UI shell (Task 5).
 *
 * Implements the approved mockup at desktop width:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ activity │ tab bar                                            │
 *   │   rail   ├────────────────────────────────────────────────────┤
 *   │          │ sidebar │ content                                  │
 *   │          │  (tree) │  (rendered file)                         │
 *   │          ├─────────┴──────────────────────────────────────────┤
 *   │          │ status bar                                         │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Acceptance (Task 5):
 *  - dark theme by default → applied by setting `theme-dark` on <html>;
 *  - activity rail → Files, Search (live /api/search pane), Graph
 *    (backlinks + outline via /api/backlinks), and Plugins (/api/plugins
 *    matrix) — each icon switches the sidebar pane, clicking the active
 *    icon toggles the sidebar;
 *  - tabs / expanded folders / selected pane persist across refresh via
 *    sessionStorage (FR-2.3);
 *  - collapsible file tree with folder icons → FileTree.tsx;
 *  - tab bar → TabBar.tsx (click file → opens in new tab; click tab →
 *    switches active);
 *  - status bar → StatusBar.tsx (vault path + file count).
 *
 * The Shell is **self-contained** — it fetches all of its data from the
 * server's `/api/*` endpoints, so `main.tsx` can mount it directly.
 *
 * Task 20 (responsive) — overlays collapse behaviour on top of this
 * skeleton:
 *   - `useViewport()` classifies `window.innerWidth` as desktop / tablet
 *     / mobile.
 *   - At desktop (≥1024px) the layout is the original grid above.
 *   - At tablet (768–1023px) the sidebar collapses; the user re-opens it
 *     via the "Files" activity icon or the hamburger toggle.
 *   - At mobile (<768px) the sidebar becomes a `SidebarDrawer` overlay
 *     — modal, full-height, with a backdrop. Picking a file auto-closes
 *     it so the user sees what they tapped.
 *   - The `data-layout` and `data-sidebar-open` attributes on the shell
 *     root drive the responsive.css media queries.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ApiError,
  displayName,
  fetchAuthIdentity,
  fetchBacklinks,
  fetchSearch,
  fetchTree,
  fetchPayloadFor,
  fetchWorkspace,
  postLogout,
  type AuthUser,
  type AnyFilePayload,
  type BacklinksResponse,
  type MarkdownFilePayload,
  type UnknownPreviewPayload,
  type SearchResponse,
  type TreeNode,
} from '../api.js';
import { useConflictResolve } from '../hooks/useConflictResolve.js';
import { useLiveUpdates, type FileChangedEvent } from '../hooks/useLiveUpdates.js';
import { useTheme } from '../hooks/useTheme.js';
import {
  classifyViewport,
  sidebarHiddenByDefault,
  sidebarRendersAsDrawer,
  useViewport,
  type ViewportLayout,
} from '../hooks/useViewport.js';

// NOTE: the Obsidian variable stylesheet + responsive overrides are loaded
// once at the HTML entry point (`apps/web/index.html`) so they are
// available to every component. We intentionally do not import them here
// — vitest runs in the `node` environment and would refuse to resolve a
// `.css` import at unit-test load time. See
// `apps/web/src/styles/obsidian-vars.css` and
// `apps/web/src/styles/responsive.css`.
import { CanvasViewer, type Canvas } from './CanvasViewer.js';
import { ConflictDialog } from './ConflictDialog.js';
import { ExcalidrawViewer } from './ExcalidrawViewer.js';
import { FileTree, collectFiles } from './FileTree.js';
import {
  MarkdownEditor,
  clearDraft,
  hasDraft,
  putMarkdownFile,
  type EditorHandle,
  type MarkdownWriteError,
} from './MarkdownEditor.js';
import { PluginMatrixPane, usePluginMatrix } from './PluginMatrixPane.js';
import { QuickSwitcher } from './QuickSwitcher.js';
import { SidebarDrawer } from './SidebarDrawer.js';
import { StatusBar } from './StatusBar.js';
import { TabBar, type TabBarState, closeTab, openTab, switchTab, tabLabel } from './TabBar.js';
import { UnknownViewer } from './UnknownViewer.js';

interface ShellProps {
  /** Vault root path label for the status bar. Defaults to a placeholder. */
  readonly vaultPath?: string;
  /** Optional pre-loaded tree (used by tests). */
  readonly initialTree?: readonly TreeNode[];
  /** Test seam — force a viewport width (skips `window.innerWidth`). */
  readonly viewportWidthOverride?: number;
}

/**
 * Pure responsive state machine — computes the next sidebar-open value
 * when the layout bucket changes. Exposed for unit tests.
 *
 *   desktop → sidebar is always open (no toggle needed)
 *   tablet  → preserve user's previous choice; default to closed on first
 *             entry so the content gets the full width.
 *   mobile  → always closed on layout entry; user must explicitly open
 *             the drawer.
 */
export function nextSidebarOpenForLayout(
  prevLayout: ViewportLayout,
  prevOpen: boolean,
  nextLayout: ViewportLayout,
): boolean {
  if (nextLayout === 'desktop') return true;
  if (nextLayout === 'mobile') return false;
  // tablet — preserve user choice when coming from another non-desktop
  // bucket, default to closed when arriving from desktop (avoid a
  // surprise overlay covering the content the user was reading).
  if (prevLayout === 'desktop') return false;
  return prevOpen;
}

/**
 * Apply the dark theme class on first mount. The class lives on <html> so
 * it cascades to portal-rendered children (modals, conflict dialogs).
 */
function useDarkThemeDefault(): void {
  useEffect(() => {
    const html = document.documentElement;
    if (!html.classList.contains('theme-dark') && !html.classList.contains('theme-light')) {
      html.classList.add('theme-dark');
    }
  }, []);
}

interface AuthModeResponse {
  readonly mode: string;
  readonly fsWritable?: boolean;
}

export interface ServerModeInfo {
  readonly mode: string;
  readonly fsWritable: boolean;
}

async function fetchAuthMode(signal?: AbortSignal): Promise<ServerModeInfo> {
  try {
    const init: RequestInit = {
      headers: { accept: 'application/json' },
      ...(signal ? { signal } : {}),
    };
    const res = await fetch('/api/auth/mode', init);
    if (!res.ok) return { mode: 'unknown', fsWritable: false };
    const body = (await res.json()) as AuthModeResponse;
    return { mode: body.mode, fsWritable: body.fsWritable === true };
  } catch {
    return { mode: 'offline', fsWritable: false };
  }
}

/* ── Session persistence (FR-2.3) ─────────────────────────────────────────
 *
 * Tabs, the active file, expanded tree folders, and the selected sidebar
 * pane survive a refresh via sessionStorage. The (de)serialisers are pure
 * and strict — malformed or stale payloads restore nothing rather than
 * crashing the shell. Exposed for unit tests.
 * ────────────────────────────────────────────────────────────────────────── */

export type SidebarPane = 'files' | 'search' | 'graph' | 'plugins';

export const SHELL_STATE_KEY = 'obsidianweb.shell.v1';

export interface PersistedShellState {
  readonly tabs: readonly string[];
  readonly activePath: string | null;
  readonly expanded: readonly string[];
  readonly pane: SidebarPane;
}

export function serializeShellState(state: PersistedShellState): string {
  return JSON.stringify(state);
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((s) => typeof s === 'string');
}

export function deserializeShellState(raw: string | null): PersistedShellState | null {
  if (raw === null || raw === '') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const rec = parsed as Record<string, unknown>;
  if (!isStringArray(rec.tabs)) return null;
  if (rec.activePath !== null && typeof rec.activePath !== 'string') return null;
  if (!isStringArray(rec.expanded)) return null;
  const panes: readonly SidebarPane[] = ['files', 'search', 'graph', 'plugins'];
  const pane = panes.includes(rec.pane as SidebarPane) ? (rec.pane as SidebarPane) : 'files';
  const tabs = rec.tabs;
  const activePath =
    typeof rec.activePath === 'string' && tabs.includes(rec.activePath)
      ? rec.activePath
      : (tabs[tabs.length - 1] ?? null);
  return { tabs, activePath, expanded: rec.expanded, pane };
}

function loadPersistedShellState(): PersistedShellState | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return deserializeShellState(window.sessionStorage.getItem(SHELL_STATE_KEY));
  } catch {
    return null;
  }
}

function storePersistedShellState(state: PersistedShellState): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.setItem(SHELL_STATE_KEY, serializeShellState(state));
  } catch {
    // Quota / privacy mode — persistence is best-effort.
  }
}

interface ActivityIconProps {
  readonly label: string;
  readonly active?: boolean;
  readonly onClick?: () => void;
  readonly children: JSX.Element;
}

function ActivityIcon(props: ActivityIconProps): JSX.Element {
  return (
    <button
      type="button"
      className={props.active ? 'active' : ''}
      title={props.label}
      aria-label={props.label}
      aria-pressed={props.active ?? false}
      {...(props.onClick ? { onClick: props.onClick } : {})}
    >
      {props.children}
    </button>
  );
}

interface ActivityBarProps {
  readonly pane: SidebarPane;
  readonly sidebarOpen: boolean;
  readonly onSelectPane: (pane: SidebarPane) => void;
}

function ActivityBar(props: ActivityBarProps): JSX.Element {
  const isActive = (pane: SidebarPane): boolean => props.sidebarOpen && props.pane === pane;
  return (
    <nav className="ow-activity-bar" aria-label="Activity bar">
      <ActivityIcon
        label="Files"
        active={isActive('files')}
        onClick={() => props.onSelectPane('files')}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h6l2 2h8v12a2 2 0 0 1-2 2H4z" />
        </svg>
      </ActivityIcon>
      <ActivityIcon
        label="Search"
        active={isActive('search')}
        onClick={() => props.onSelectPane('search')}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
      </ActivityIcon>
      <ActivityIcon
        label="Graph"
        active={isActive('graph')}
        onClick={() => props.onSelectPane('graph')}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="6" cy="6" r="2" />
          <circle cx="18" cy="6" r="2" />
          <circle cx="12" cy="18" r="2" />
          <path d="M7.5 7.5 11 16.5M16.5 7.5 13 16.5M8 6h8" />
        </svg>
      </ActivityIcon>
      <ActivityIcon
        label="Plugin matrix"
        active={isActive('plugins')}
        onClick={() => props.onSelectPane('plugins')}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      </ActivityIcon>
    </nav>
  );
}

/* ── Sidebar panes (Search / Graph / Plugins) ─────────────────────────── */

/** Strip HTML from a server snippet, keeping only its text. */
export function snippetToText(snippet: string): string {
  return snippet.replace(/<[^>]*>/g, '');
}

function SearchPane(props: {
  readonly onPick: (path: string, line?: number) => void;
}): JSX.Element {
  const [query, setQuery] = useState<string>('');
  const [result, setResult] = useState<SearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q === '') {
      setResult(null);
      setError(null);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetchSearch(q, controller.signal)
        .then((res) => {
          setResult(res);
          setError(null);
        })
        .catch((err: unknown) => {
          if (err instanceof Error && err.name === 'AbortError') return;
          setError(err instanceof ApiError ? `${err.code}: ${err.message}` : String(err));
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  return (
    <div data-testid="search-pane" style={{ padding: '8px 12px' }}>
      <input
        type="search"
        placeholder="Search vault…"
        aria-label="Search vault"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box' }}
      />
      {error !== null ? <p style={{ color: 'var(--text-error)' }}>{error}</p> : null}
      {result !== null ? (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {result.hits.map((hit) => (
            <li key={`${hit.path}:${hit.line}`}>
              <button
                type="button"
                onClick={() => props.onPick(hit.path, hit.line)}
                style={{ display: 'block', width: '100%', textAlign: 'left' }}
              >
                <strong>{hit.path}</strong>
                <span style={{ color: 'var(--text-faint, #6b6b6b)' }}>:{hit.line}</span>
                <br />
                <span style={{ color: 'var(--text-muted)' }}>{snippetToText(hit.snippet)}</span>
              </button>
            </li>
          ))}
          {result.hits.length === 0 ? (
            <li style={{ color: 'var(--text-muted)' }}>No matches.</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

function GraphPane(props: {
  readonly activePath: string | null;
  readonly onPick: (path: string) => void;
}): JSX.Element {
  const [result, setResult] = useState<BacklinksResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setResult(null);
    setError(null);
    if (props.activePath === null) return undefined;
    const controller = new AbortController();
    fetchBacklinks(props.activePath, controller.signal)
      .then((res) => setResult(res))
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof ApiError ? `${err.code}: ${err.message}` : String(err));
      });
    return () => controller.abort();
  }, [props.activePath]);

  if (props.activePath === null) {
    return (
      <p data-testid="graph-pane" style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>
        Open a note to see its backlinks.
      </p>
    );
  }
  return (
    <div data-testid="graph-pane" style={{ padding: '8px 12px' }}>
      {error !== null ? <p style={{ color: 'var(--text-error)' }}>{error}</p> : null}
      {result !== null ? <BacklinkSections result={result} onPick={props.onPick} /> : null}
    </div>
  );
}

/** Pure presentational body of the backlinks pane: linked mentions,
 *  unlinked mentions (plain-text occurrences of the note name that are
 *  not wikilinked — the server computes them on every /api/backlinks
 *  call), and the heading outline. Exported for tests. */
export function BacklinkSections(props: {
  readonly result: BacklinksResponse;
  readonly onPick: (path: string) => void;
}): JSX.Element {
  const { result } = props;
  return (
    <>
      <h3 style={{ margin: '4px 0' }}>Backlinks</h3>
      <ul data-testid="graph-linked" style={{ listStyle: 'none', padding: 0 }}>
        {result.linked.map((m, i) => (
          <li key={`l-${m.sourcePath}-${i}`}>
            <button type="button" onClick={() => props.onPick(m.sourcePath)}>
              {m.sourcePath}
            </button>
          </li>
        ))}
        {result.linked.length === 0 ? (
          <li style={{ color: 'var(--text-muted)' }}>No linked mentions.</li>
        ) : null}
      </ul>
      <h3 style={{ margin: '4px 0' }}>Unlinked mentions</h3>
      <ul data-testid="graph-unlinked" style={{ listStyle: 'none', padding: 0 }}>
        {result.unlinked.map((m, i) => (
          <li key={`u-${m.sourcePath}-${i}`}>
            <button type="button" onClick={() => props.onPick(m.sourcePath)}>
              {m.sourcePath}
            </button>
            {m.snippet ? (
              <span style={{ color: 'var(--text-muted)', marginLeft: '6px' }}>{m.snippet}</span>
            ) : null}
          </li>
        ))}
        {result.unlinked.length === 0 ? (
          <li style={{ color: 'var(--text-muted)' }}>No unlinked mentions.</li>
        ) : null}
      </ul>
      <h3 style={{ margin: '4px 0' }}>Outline</h3>
      <ul data-testid="graph-outline" style={{ listStyle: 'none', padding: 0 }}>
        {result.outline.map((h) => (
          <li key={h.slug} style={{ paddingLeft: `${(h.level - 1) * 12}px` }}>
            {h.text}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * FR-11.3 decision table for an incoming external-change event. Exported
 * for unit tests.
 *
 *  - not the open file, or an unlink → nothing (cache invalidation and the
 *    tree refresh are handled separately).
 *  - open file, clean buffer, `change` → non-blocking reload toast; never
 *    silently swap content under the reader.
 *  - open file, dirty buffer (or pending draft) → auto-refetch so the
 *    editor's draft-preserving banner + 409 conflict flow can warn about
 *    the pending edits.
 */
export type ExternalChangeAction = 'none' | 'show-reload-toast' | 'auto-refetch';

export function classifyExternalChange(
  event: FileChangedEvent,
  activePath: string | null,
  dirty: boolean,
): ExternalChangeAction {
  if (event.changeKind === 'unlink' || activePath === null || event.path !== activePath) {
    return 'none';
  }
  if (event.changeKind === 'change' && !dirty) return 'show-reload-toast';
  return 'auto-refetch';
}

/**
 * Non-blocking FR-11.3 toast: "File changed on disk — reload?" with a
 * Reload action. Rendered above the status bar; exported for unit tests.
 */
export function ReloadToast(props: {
  readonly path: string;
  readonly onReload: () => void;
  readonly onDismiss: () => void;
}): JSX.Element {
  return (
    <div
      role="status"
      data-testid="reload-toast"
      style={{
        position: 'fixed',
        bottom: 40,
        right: 16,
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        borderRadius: 6,
        background: 'var(--background-secondary, #262626)',
        border: '1px solid var(--background-modifier-border, #3f3f3f)',
        color: 'var(--text-normal, #dcddde)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        maxWidth: '80vw',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        File changed on disk — reload? <code style={{ fontSize: 12 }}>{props.path}</code>
      </span>
      <button type="button" data-testid="reload-toast-reload" onClick={props.onReload}>
        Reload
      </button>
      <button
        type="button"
        data-testid="reload-toast-dismiss"
        aria-label="Dismiss"
        onClick={props.onDismiss}
      >
        ✕
      </button>
    </div>
  );
}

/**
 * Plugins tab — thin wrapper around the standalone PluginMatrixPane
 * (the task-18 deliverable: full table with status badges, notes, and
 * diagnostics per FR-8.5/FR-9.1). Fetching lives in `usePluginMatrix`.
 */
function PluginsPane(): JSX.Element {
  const matrix = usePluginMatrix();
  return (
    <div data-testid="plugins-pane">
      <PluginMatrixPane data={matrix.data} loading={matrix.loading} errorMessage={matrix.error} />
    </div>
  );
}

const PANE_TITLES: Readonly<Record<SidebarPane, string>> = {
  files: 'Files',
  search: 'Search',
  graph: 'Graph',
  plugins: 'Plugins',
};

/** Narrow a cached payload to the /api/preview unknown-file union. */
export function isUnknownPayload(payload: AnyFilePayload): payload is UnknownPreviewPayload {
  switch (payload.kind) {
    case 'unknown-text':
    case 'unknown-image':
    case 'unknown-pdf':
    case 'unknown-svg':
    case 'unknown-audio':
    case 'unknown-video':
    case 'unknown-binary':
    case 'unknown-oversized':
      return true;
    default:
      return false;
  }
}

interface ContentPaneProps {
  readonly activePath: string | null;
  readonly file: AnyFilePayload | null;
  readonly loading: boolean;
  readonly error: string | null;
  /** Whether the server accepts PUT /api/file (edit affordances shown). */
  readonly fsWritable: boolean;
  /** Open a vault file (canvas file-node clicks route here). */
  readonly onOpenFile?: (path: string) => void;
  /** Refetch a file payload after a save / conflict resolution. */
  readonly onRefetchFile?: (path: string) => void;
  /** Dirty-state reporting for tab-close confirm / beforeunload guard. */
  readonly onDirtyChange?: (path: string, dirty: boolean) => void;
  /** Pending scroll-to-line request from a search-hit click (FR-12.2). */
  readonly scrollTarget?: { readonly path: string; readonly line: number } | null;
  /** Called once the view has scrolled to `scrollTarget`. */
  readonly onScrolledToTarget?: () => void;
}

/**
 * Markdown tab content — the read/edit surface for one `.md` payload.
 *
 * Composes MarkdownEditor (Task 12) with the conflict-resolution flow
 * (Task 13): saves go through PUT /api/file with optimistic locking, a
 * 409 opens the ConflictDialog, and both "Overwrite" and "Reload and
 * re-edit" rebase the editor baseline through the imperative handle.
 */
export function MarkdownContent(props: {
  readonly file: MarkdownFilePayload;
  readonly fsWritable: boolean;
  readonly onRefetchFile?: ((path: string) => void) | undefined;
  readonly onDirtyChange?: ((path: string, dirty: boolean) => void) | undefined;
  /** Scroll the rendered view to this 1-based source line (FR-12.2). */
  readonly scrollToLine?: number | undefined;
  /** Notified after the scroll happened (or was found impossible). */
  readonly onScrolledToLine?: (() => void) | undefined;
}): JSX.Element {
  const editorRef = useRef<EditorHandle | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // FR-12.2: after a search-hit click the rendered note scrolls to the
  // block whose data-line is closest at-or-before the matching line.
  const { scrollToLine, onScrolledToLine } = props;
  useEffect(() => {
    if (scrollToLine === undefined) return;
    const root = mainRef.current;
    if (!root) return;
    const blocks = Array.from(root.querySelectorAll<HTMLElement>('[data-line]'));
    let best: HTMLElement | null = null;
    let bestLine = -1;
    for (const el of blocks) {
      const n = Number(el.getAttribute('data-line'));
      if (!Number.isFinite(n)) continue;
      if (n <= scrollToLine && n > bestLine) {
        best = el;
        bestLine = n;
      }
    }
    const target = best ?? blocks[0] ?? null;
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'center' });
    }
    onScrolledToLine?.();
  }, [scrollToLine, onScrolledToLine, props.file.path, props.file.html]);
  const conflict = useConflictResolve({
    saveFile: putMarkdownFile,
    onReload: (next) => {
      editorRef.current?.applyResolution(next);
      props.onRefetchFile?.(props.file.path);
    },
    onOverwritten: (success) => {
      const draft = editorRef.current?.getBuffer();
      if (draft !== undefined) {
        editorRef.current?.applyResolution({
          content: draft,
          mtimeMs: success.mtimeMs,
          sha256: success.sha256,
        });
      }
      props.onRefetchFile?.(props.file.path);
    },
  });
  return (
    <main ref={mainRef} className="ow-content" aria-live="polite" data-testid="markdown-viewer">
      <MarkdownEditor
        ref={editorRef}
        payload={props.file}
        fsWritable={props.fsWritable}
        onConflict={conflict.openConflict}
        onSaved={() => {
          setSaveError(null);
          props.onRefetchFile?.(props.file.path);
        }}
        onError={(err: MarkdownWriteError) => setSaveError(`${err.code}: ${err.message}`)}
        onDirtyChange={(dirty: boolean) => props.onDirtyChange?.(props.file.path, dirty)}
      />
      {saveError !== null ? (
        <p data-testid="markdown-save-error" style={{ color: 'var(--text-error)' }}>
          Save failed — {saveError}
        </p>
      ) : null}
      <ConflictDialog
        state={conflict.state}
        onOverwrite={conflict.overwrite}
        onReload={conflict.reload}
        onCancel={conflict.dismiss}
      />
    </main>
  );
}

/**
 * Canvas tab content — viewer plus (when the server is writable) the
 * CanvasViewer's editor mode wired to PUT /api/file with the same
 * optimistic-lock envelope the markdown editor uses (FR-4.3/4.4).
 */
export function CanvasContent(props: {
  readonly file: { readonly path: string; readonly sha256: string; readonly mtimeMs: number };
  readonly initialCanvas: Canvas;
  readonly fsWritable: boolean;
  readonly onOpenFile?: ((path: string) => void) | undefined;
  readonly onRefetchFile?: ((path: string) => void) | undefined;
}): JSX.Element {
  const [canvas, setCanvas] = useState<Canvas>(props.initialCanvas);
  // Optimistic-lock pair, rebased on every successful save.
  const [lock, setLock] = useState({ mtimeMs: props.file.mtimeMs, sha256: props.file.sha256 });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Rebase local state when the payload changes underneath us (tab switch
  // or a refetch after save/conflict).
  useEffect(() => {
    setCanvas(props.initialCanvas);
    setLock({ mtimeMs: props.file.mtimeMs, sha256: props.file.sha256 });
    setSaveError(null);
  }, [props.file.path, props.file.sha256, props.file.mtimeMs, props.initialCanvas]);

  const handleSave = useCallback(
    (next: Canvas): void => {
      setSaving(true);
      setSaveError(null);
      // Same envelope as the markdown save; the server dispatches `.canvas`
      // paths to the JSON Canvas validator + canonical serialiser.
      void putMarkdownFile({
        path: props.file.path,
        content: JSON.stringify(next, null, 2) + '\n',
        expectedMtime: lock.mtimeMs,
        expectedHash: lock.sha256,
      }).then((result) => {
        setSaving(false);
        if (result.ok) {
          setLock({ mtimeMs: result.value.mtimeMs, sha256: result.value.sha256 });
          props.onRefetchFile?.(props.file.path);
          return;
        }
        if ('conflict' in result && result.conflict) {
          setSaveError(
            'CONFLICT: the canvas changed on disk since you opened it. Reload the tab to pick up the latest version.',
          );
          return;
        }
        if ('error' in result && result.error) {
          setSaveError(`${result.error.code}: ${result.error.message}`);
        }
      });
    },
    [props.file.path, lock.mtimeMs, lock.sha256, props.onRefetchFile],
  );

  return (
    <main className="ow-content" aria-live="polite" data-testid="canvas-viewer">
      <CanvasViewer
        canvas={canvas}
        editable={props.fsWritable}
        onChange={setCanvas}
        onSave={handleSave}
        onOpenFile={(filePath) =>
          props.onOpenFile?.(filePath.startsWith('/') ? filePath : `/${filePath}`)
        }
      />
      {saving ? (
        <p data-testid="canvas-saving" style={{ color: 'var(--text-muted)' }}>
          Saving…
        </p>
      ) : null}
      {saveError !== null ? (
        <p data-testid="canvas-save-error" style={{ color: 'var(--text-error)' }}>
          Save failed — {saveError}
        </p>
      ) : null}
    </main>
  );
}

/* ── Global keymap (FR-2.4 / FR-3.4 / FR-12.2) ───────────────────────── */

export type ShortcutAction = 'quick-switcher' | 'search-focus' | null;

/**
 * Classify a global keydown. Ctrl on Linux/Windows, Cmd on macOS.
 * Ctrl/Cmd+E (reading/source toggle) is handled inside MarkdownEditor,
 * which owns the mode state; everything app-level is decided here.
 * Exported for tests.
 */
export function classifyShortcut(e: {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
}): ShortcutAction {
  const mod = e.ctrlKey || e.metaKey;
  if (!mod) return null;
  const key = e.key.toLowerCase();
  if (key === 'o' && !e.shiftKey) return 'quick-switcher';
  if (key === 'f' && e.shiftKey) return 'search-focus';
  return null;
}

/**
 * Message shown before closing a tab whose buffer has unsaved changes
 * (FR-11.3). Exported for tests.
 */
export function confirmDiscardMessage(filePath: string): string {
  return `"${tabLabel(filePath)}" has unsaved changes. Discard them and close the tab?`;
}

export function ContentPane(props: ContentPaneProps): JSX.Element {
  if (props.activePath === null) {
    return (
      <main className="ow-content" aria-live="polite">
        <p style={{ color: 'var(--text-muted)' }}>
          Select a file from the tree to open it in a tab.
        </p>
      </main>
    );
  }
  if (props.loading) {
    return (
      <main className="ow-content" aria-live="polite">
        <p style={{ color: 'var(--text-muted)' }}>Loading {props.activePath}…</p>
      </main>
    );
  }
  if (props.error !== null) {
    return (
      <main className="ow-content" aria-live="polite">
        <p style={{ color: 'var(--text-error)' }}>Error: {props.error}</p>
      </main>
    );
  }
  if (props.file === null) {
    return (
      <main className="ow-content" aria-live="polite">
        <p style={{ color: 'var(--text-muted)' }}>(empty)</p>
      </main>
    );
  }
  if (isUnknownPayload(props.file)) {
    // Non-md/canvas/excalidraw files (images, PDFs, SVGs, arbitrary
    // binaries) come from /api/preview as typed payloads; UnknownViewer
    // picks the right inline rendering.
    return (
      <main className="ow-content" aria-live="polite" data-testid="unknown-file-pane">
        <UnknownViewer payload={props.file} />
      </main>
    );
  }
  if (props.file.kind === 'markdown') {
    return (
      <MarkdownContent
        file={props.file}
        fsWritable={props.fsWritable}
        onRefetchFile={props.onRefetchFile}
        onDirtyChange={props.onDirtyChange}
        scrollToLine={
          props.scrollTarget && props.scrollTarget.path === props.file.path
            ? props.scrollTarget.line
            : undefined
        }
        onScrolledToLine={props.onScrolledToTarget}
      />
    );
  }
  if (props.file.kind === 'excalidraw') {
    return (
      <main className="ow-content" aria-live="polite" data-testid="excalidraw-viewer">
        <ExcalidrawViewer scene={props.file.scene} />
      </main>
    );
  }
  if (props.file.kind === 'excalidraw-broken') {
    return (
      <main className="ow-content" aria-live="polite" data-testid="excalidraw-broken-viewer">
        <p style={{ color: 'var(--text-error)' }}>
          Could not parse Excalidraw scene ({props.file.errorCode}): {props.file.errorMessage}
        </p>
        <pre style={{ whiteSpace: 'pre-wrap' }}>{props.file.raw}</pre>
      </main>
    );
  }
  if (props.file.kind === 'text' && props.file.path.toLowerCase().endsWith('.canvas')) {
    const canvas = parseCanvasText(props.file.content);
    if (canvas !== null) {
      return (
        <CanvasContent
          file={props.file}
          initialCanvas={canvas}
          fsWritable={props.fsWritable}
          onOpenFile={props.onOpenFile}
          onRefetchFile={props.onRefetchFile}
        />
      );
    }
    // Unparseable canvas — fall through to the raw text rendering below.
  }
  return (
    <main className="ow-content" aria-live="polite">
      <pre style={{ whiteSpace: 'pre-wrap' }}>{props.file.content}</pre>
    </main>
  );
}

/**
 * Parse a `.canvas` file body (JSON-Canvas) into the viewer's `Canvas`
 * shape. Strict enough to refuse garbage (null on any malformation) while
 * tolerating extra fields. Pure; exported for unit tests.
 */
export function parseCanvasText(content: string): Canvas | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const rec = parsed as { nodes?: unknown; edges?: unknown };
  const nodes = Array.isArray(rec.nodes) ? rec.nodes : [];
  const edges = Array.isArray(rec.edges) ? rec.edges : [];
  const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null;
  for (const n of nodes) {
    if (!isRecord(n) || typeof n.id !== 'string' || typeof n.type !== 'string') return null;
  }
  for (const e of edges) {
    if (
      !isRecord(e) ||
      typeof e.id !== 'string' ||
      typeof e.fromNode !== 'string' ||
      typeof e.toNode !== 'string'
    ) {
      return null;
    }
  }
  return { nodes, edges } as Canvas;
}

export function Shell(props: ShellProps = {}): JSX.Element {
  useDarkThemeDefault();
  // Vault theme bundle (Task 15/20): fetches /api/theme once, injects the
  // sanitized CSS into <head>, and flips theme-dark/theme-light per the
  // vault's appearance.json. Degrades to the dark default on any failure.
  useTheme();

  const [tree, setTree] = useState<readonly TreeNode[] | null>(props.initialTree ?? null);
  const [treeError, setTreeError] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<string>('…');
  const [fsWritable, setFsWritable] = useState<boolean>(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  // Restore tabs / expanded folders / pane from sessionStorage (FR-2.3).
  const [persisted] = useState<PersistedShellState | null>(() => loadPersistedShellState());
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set(persisted?.expanded ?? []),
  );
  const [tabState, setTabState] = useState<TabBarState>(() =>
    persisted === null
      ? { tabs: [], activePath: null }
      : {
          tabs: persisted.tabs.map((p) => ({ path: p, title: tabLabel(p) })),
          activePath: persisted.activePath,
        },
  );
  const [pane, setPane] = useState<SidebarPane>(persisted?.pane ?? 'files');
  const [fileCache, setFileCache] = useState<ReadonlyMap<string, AnyFilePayload>>(new Map());
  /** Pending scroll-to-line request from a search-hit click (FR-12.2). */
  const [scrollTarget, setScrollTarget] = useState<{ path: string; line: number } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState<boolean>(false);

  const viewportOpts =
    props.viewportWidthOverride !== undefined
      ? { initialWidth: props.viewportWidthOverride, subscribe: false }
      : {};
  const viewport = useViewport(viewportOpts);
  const layout = viewport.layout;

  // Sidebar visibility. Initial value depends on the first layout bucket:
  // desktop = open, tablet/mobile = closed (user opts in).
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(
    () => !sidebarHiddenByDefault(classifyViewport(viewport.width)),
  );
  // Track the previous layout so layout transitions can apply the right
  // default (see `nextSidebarOpenForLayout`).
  const [lastLayout, setLastLayout] = useState<ViewportLayout>(layout);
  useEffect(() => {
    if (layout === lastLayout) return;
    setSidebarOpen((prev) => nextSidebarOpenForLayout(lastLayout, prev, layout));
    setLastLayout(layout);
  }, [layout, lastLayout]);

  // Fetch tree + auth mode on mount unless tree was injected by tests.
  useEffect(() => {
    const controller = new AbortController();
    if (props.initialTree === undefined) {
      fetchTree(controller.signal)
        .then((res) => {
          setTree(res.entries);
          setTreeError(null);
        })
        .catch((err: unknown) => {
          if (err instanceof Error && err.name === 'AbortError') return;
          setTreeError(err instanceof ApiError ? `${err.code}: ${err.message}` : String(err));
        });
    }
    fetchAuthMode(controller.signal).then((info) => {
      setAuthMode(info.mode);
      setFsWritable(info.fsWritable);
      // Identity display (FR-10.4): only meaningful when an auth layer is
      // actually in front of us.
      if (info.mode === 'oidc' || info.mode === 'proxy') {
        void fetchAuthIdentity(controller.signal).then(setUser);
      }
    });
    return () => controller.abort();
  }, [props.initialTree]);

  // Persist shell state (FR-2.3). Best-effort; failures are swallowed.
  useEffect(() => {
    storePersistedShellState({
      tabs: tabState.tabs.map((t) => t.path),
      activePath: tabState.activePath,
      expanded: Array.from(expanded),
      pane,
    });
  }, [tabState, expanded, pane]);

  const handleToggle = useCallback((dirPath: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(dirPath)) next.delete(dirPath);
      else next.add(dirPath);
      return next;
    });
  }, []);

  const ensureLoaded = useCallback(
    (filePath: string) => {
      if (fileCache.has(filePath)) return;
      setFileLoading(true);
      setFileError(null);
      fetchPayloadFor(filePath)
        .then((payload) => {
          setFileCache((prev) => {
            const next = new Map(prev);
            next.set(filePath, payload);
            return next;
          });
          setFileLoading(false);
        })
        .catch((err: unknown) => {
          setFileError(err instanceof ApiError ? `${err.code}: ${err.message}` : String(err));
          setFileLoading(false);
        });
    },
    [fileCache],
  );

  const handlePick = useCallback(
    (filePath: string) => {
      setTabState((prev) => openTab(prev, filePath));
      ensureLoaded(filePath);
    },
    [ensureLoaded],
  );

  // FR-8.4: on a first-load session (no sessionStorage state), seed the
  // tab bar from the vault's `.obsidian/workspace.json`. Best-effort:
  // unknown paths are dropped, failures are swallowed, and a session the
  // user already interacted with is never overwritten.
  const workspaceSeededRef = useRef(false);
  useEffect(() => {
    if (workspaceSeededRef.current) return undefined;
    if (persisted !== null) {
      workspaceSeededRef.current = true;
      return undefined;
    }
    if (tree === null) return undefined; // wait for the tree to validate paths
    workspaceSeededRef.current = true;
    const known = new Set(collectFiles(tree).map((n) => n.path));
    const controller = new AbortController();
    fetchWorkspace(controller.signal)
      .then((ws) => {
        const files = ws.openFiles.filter((p) => known.has(p));
        if (files.length === 0) return;
        const active =
          ws.activeFile !== null && known.has(ws.activeFile) ? ws.activeFile : (files[0] as string);
        setTabState((prev) =>
          prev.tabs.length > 0
            ? prev
            : { tabs: files.map((p) => ({ path: p, title: tabLabel(p) })), activePath: active },
        );
        ensureLoaded(active);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [persisted, tree, ensureLoaded]);

  const handleSwitch = useCallback(
    (filePath: string) => {
      setTabState((prev) => switchTab(prev, filePath));
      ensureLoaded(filePath);
    },
    [ensureLoaded],
  );

  // Paths whose editor buffer currently differs from disk (FR-11.3).
  const [dirtyPaths, setDirtyPaths] = useState<ReadonlySet<string>>(() => new Set());
  const handleDirtyChange = useCallback((filePath: string, dirty: boolean) => {
    setDirtyPaths((prev) => {
      if (prev.has(filePath) === dirty) return prev;
      const next = new Set(prev);
      if (dirty) next.add(filePath);
      else next.delete(filePath);
      return next;
    });
  }, []);

  const handleClose = useCallback(
    (filePath: string) => {
      if (dirtyPaths.has(filePath) || hasDraft(filePath)) {
        const ok =
          typeof window === 'undefined' || typeof window.confirm !== 'function'
            ? true
            : window.confirm(confirmDiscardMessage(filePath));
        if (!ok) return;
        clearDraft(filePath);
        handleDirtyChange(filePath, false);
      }
      setTabState((prev) => closeTab(prev, filePath));
    },
    [dirtyPaths, handleDirtyChange],
  );

  // Refresh-guard (FR-11.3): while any buffer is dirty, warn before the
  // page unloads. Browsers show their own generic message; the point is
  // the interception, not the wording.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (dirtyPaths.size === 0) return undefined;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirtyPaths]);

  // Refresh one file's cached payload (post-save / conflict resolution).
  const refetchFile = useCallback((filePath: string) => {
    fetchPayloadFor(filePath)
      .then((payload) => {
        setFileCache((prev) => {
          const next = new Map(prev);
          next.set(filePath, payload);
          return next;
        });
      })
      .catch(() => undefined);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  // Quick switcher (FR-2.4) + search focus (FR-12.2) global keymap.
  const [showSwitcher, setShowSwitcher] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onKey = (e: KeyboardEvent): void => {
      const action = classifyShortcut(e);
      if (action === null) return;
      e.preventDefault();
      if (action === 'quick-switcher') {
        setShowSwitcher((prev) => !prev);
      } else {
        // search-focus: reveal the search pane, then focus its input.
        setPane('search');
        setSidebarOpen(true);
        window.setTimeout(() => {
          const input = document.querySelector<HTMLInputElement>(
            '[data-testid="search-pane"] input',
          );
          input?.focus();
        }, 0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const switcherFiles = useMemo(
    () => (tree ? collectFiles(tree).map((n) => n.path) : []),
    [tree],
  );

  // FR-10.4: end the session server-side, then reload so the auth gate
  // takes over (oidc redirects to login; open mode just re-renders).
  const handleLogout = useCallback(() => {
    void postLogout().then(() => {
      if (typeof window !== 'undefined') window.location.reload();
    });
  }, []);

  // Restored sessions have an active tab but an empty file cache — hydrate
  // it. Idempotent: ensureLoaded no-ops for already-cached paths.
  useEffect(() => {
    if (tabState.activePath !== null) ensureLoaded(tabState.activePath);
  }, [tabState.activePath, ensureLoaded]);

  // Live updates (FR-11.3): external edits invalidate cached payloads and
  // refresh the tree. For the OPEN file the behaviour splits on buffer
  // state — see classifyExternalChange:
  //   - clean buffer → non-blocking "File changed on disk — reload?" toast;
  //     the content is NOT swapped under the reader until they act.
  //   - dirty buffer / pending draft → auto-refetch, which feeds
  //     MarkdownEditor's draft-preserving external-change banner
  //     (Reload / Keep draft; the next save 409s into the conflict dialog).
  const activePathRef = useRef(tabState.activePath);
  activePathRef.current = tabState.activePath;
  const dirtyPathsRef = useRef(dirtyPaths);
  dirtyPathsRef.current = dirtyPaths;
  const [reloadToastPath, setReloadToastPath] = useState<string | null>(null);
  useLiveUpdates(
    useCallback((event: FileChangedEvent) => {
      const action = classifyExternalChange(
        event,
        activePathRef.current,
        dirtyPathsRef.current.has(event.path) || hasDraft(event.path),
      );
      setFileCache((prev) => {
        if (!prev.has(event.path)) return prev;
        // Keep the clean active buffer's current content on screen — the
        // toast owns the refresh decision.
        if (action === 'show-reload-toast') return prev;
        const next = new Map(prev);
        next.delete(event.path);
        return next;
      });
      if (event.changeKind !== 'change') {
        fetchTree()
          .then((res) => setTree(res.entries))
          .catch(() => undefined);
      }
      if (action === 'show-reload-toast') {
        setReloadToastPath(event.path);
      } else if (action === 'auto-refetch') {
        fetchPayloadFor(event.path)
          .then((payload) => {
            setFileCache((prev) => {
              const next = new Map(prev);
              next.set(event.path, payload);
              return next;
            });
          })
          .catch(() => undefined);
      }
    }, []),
  );
  const handleToastReload = useCallback(() => {
    setReloadToastPath((path) => {
      if (path !== null) refetchFile(path);
      return null;
    });
  }, [refetchFile]);
  const handleToastDismiss = useCallback(() => {
    setReloadToastPath(null);
  }, []);

  // Activity bar: clicking the current pane's icon toggles the sidebar,
  // clicking another pane switches to it and makes sure the sidebar shows.
  const handleSelectPane = useCallback(
    (next: SidebarPane) => {
      if (next === pane) {
        setSidebarOpen((prev) => !prev);
        return;
      }
      setPane(next);
      setSidebarOpen(true);
    },
    [pane],
  );

  const handleCloseDrawer = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  // On mobile, picking a file should auto-close the drawer so the user
  // sees the file they just chose. On desktop/tablet we leave the
  // sidebar's open state alone.
  const handlePickResponsive = useCallback(
    (filePath: string, line?: number) => {
      handlePick(filePath);
      // Search hits carry the matching line (FR-12.2) — remember it so the
      // markdown view can scroll there once the payload renders.
      setScrollTarget(line !== undefined ? { path: filePath, line } : null);
      if (sidebarRendersAsDrawer(layout)) {
        setSidebarOpen(false);
      }
    },
    [handlePick, layout],
  );

  const fileCount = useMemo(() => (tree ? collectFiles(tree).length : 0), [tree]);
  const activeFile: AnyFilePayload | null = useMemo(() => {
    if (tabState.activePath === null) return null;
    return fileCache.get(tabState.activePath) ?? null;
  }, [tabState.activePath, fileCache]);

  const showInlineSidebar = layout === 'desktop' || (layout === 'tablet' && sidebarOpen);
  const showDrawer = layout === 'mobile' && sidebarOpen;

  const sidebarContents = (
    <>
      <div className="ow-sidebar-header">{PANE_TITLES[pane]}</div>
      {pane === 'files' ? (
        <>
          {tree === null && treeError === null ? (
            <p style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>Loading…</p>
          ) : null}
          {treeError !== null ? (
            <p style={{ padding: '8px 12px', color: 'var(--text-error)' }}>{treeError}</p>
          ) : null}
          {tree !== null ? (
            <div data-testid="file-tree">
              <FileTree
                nodes={tree}
                activePath={tabState.activePath}
                expanded={expanded}
                onToggle={handleToggle}
                onPick={handlePickResponsive}
              />
            </div>
          ) : null}
        </>
      ) : null}
      {pane === 'search' ? <SearchPane onPick={handlePickResponsive} /> : null}
      {pane === 'graph' ? (
        <GraphPane activePath={tabState.activePath} onPick={handlePickResponsive} />
      ) : null}
      {pane === 'plugins' ? <PluginsPane /> : null}
    </>
  );

  return (
    <div
      className="ow-shell"
      data-testid="shell-root"
      data-layout={layout}
      data-sidebar-open={sidebarOpen ? 'true' : 'false'}
    >
      <ActivityBar pane={pane} sidebarOpen={sidebarOpen} onSelectPane={handleSelectPane} />
      {layout !== 'desktop' ? (
        <button
          type="button"
          className="ow-sidebar-toggle"
          aria-label={sidebarOpen ? 'Hide file tree' : 'Show file tree'}
          aria-expanded={sidebarOpen}
          aria-controls="ow-sidebar-region"
          onClick={handleToggleSidebar}
          data-testid="sidebar-toggle"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      ) : null}
      <TabBar
        tabs={tabState.tabs}
        activePath={tabState.activePath}
        onSwitch={handleSwitch}
        onClose={handleClose}
      />
      {showInlineSidebar ? (
        <aside id="ow-sidebar-region" className="ow-sidebar" aria-label="Vault file tree">
          {sidebarContents}
        </aside>
      ) : null}
      <ContentPane
        activePath={tabState.activePath}
        file={activeFile}
        loading={fileLoading && !fileCache.has(tabState.activePath ?? '')}
        error={fileError}
        fsWritable={fsWritable}
        onOpenFile={handlePickResponsive}
        onRefetchFile={refetchFile}
        onDirtyChange={handleDirtyChange}
        scrollTarget={scrollTarget}
        onScrolledToTarget={() => setScrollTarget(null)}
      />
      <StatusBar
        vaultPath={props.vaultPath ?? '/vault'}
        fileCount={fileCount}
        authMode={authMode}
        activePath={tabState.activePath}
        connection={treeError === null ? 'connected' : 'error'}
        {...(treeError !== null ? { errorText: treeError } : {})}
        user={
          user === null
            ? null
            : { label: displayName(user), detail: user.email ?? user.sub }
        }
        {...(authMode === 'oidc' ? { onLogout: handleLogout } : {})}
      />
      {reloadToastPath !== null ? (
        <ReloadToast
          path={reloadToastPath}
          onReload={handleToastReload}
          onDismiss={handleToastDismiss}
        />
      ) : null}
      <SidebarDrawer open={showDrawer} onClose={handleCloseDrawer} ariaLabel="Vault file tree">
        {sidebarContents}
      </SidebarDrawer>
      {showSwitcher ? (
        <QuickSwitcher
          files={switcherFiles}
          onPick={handlePick}
          onClose={() => setShowSwitcher(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * Pure helper exported for the smoke test. Walks a sequence of user picks
 * + tab clicks and returns the final `(tabs, active)` state — verifies the
 * open/switch/close contract without rendering React.
 */
export interface ShellInteraction {
  readonly kind: 'open' | 'switch' | 'close';
  readonly path: string;
}

export function runShellInteractions(
  initial: TabBarState,
  interactions: readonly ShellInteraction[],
): TabBarState {
  let state = initial;
  for (const step of interactions) {
    if (step.kind === 'open') state = openTab(state, step.path);
    else if (step.kind === 'switch') state = switchTab(state, step.path);
    else state = closeTab(state, step.path);
  }
  return state;
}

export default Shell;
